import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useTheme } from './ThemeContext';

const DEFAULT_LIGHT_MODE_COLOUR = '#E5E7EB';
const DEFAULT_DARK_MODE_COLOUR = '#374151';

const GET_TAG_TYPE_BADGE_COLOURS = gql`
    query GetTagTypeBadgeColours {
        TagTypeSettings {
            id
            name
            lightModeColour
            darkModeColour
        }
    }
`;

type TagTypeColourSetting = {
    name?: string | null;
    lightModeColour?: string | null;
    darkModeColour?: string | null;
};

type TagTypeColourMap = Record<string, TagTypeColourSetting>;

const TagTypeColourContext = createContext<TagTypeColourMap>({});

export const getReadableTextColour = (background: string): '#000000' | '#FFFFFF' => {
    const match = /^#([0-9A-Fa-f]{6})$/.exec(background);
    if (!match) return '#000000';

    const channels = [0, 2, 4].map(offset => {
        const channel = parseInt(match[1].slice(offset, offset + 2), 16) / 255;
        return channel <= 0.04045
            ? channel / 12.92
            : Math.pow((channel + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
    return luminance > 0.179 ? '#000000' : '#FFFFFF';
};

export const TagTypeColourProvider = ({ children }: { children: ReactNode }) => {
    const { data } = useQuery(GET_TAG_TYPE_BADGE_COLOURS, {
        fetchPolicy: 'cache-and-network',
    });
    const settings = useMemo<TagTypeColourMap>(() => {
        const result: TagTypeColourMap = {};
        for (const setting of (data?.TagTypeSettings ?? []) as TagTypeColourSetting[]) {
            if (setting.name) result[setting.name.toLowerCase()] = setting;
        }
        return result;
    }, [data]);

    return (
        <TagTypeColourContext.Provider value={settings}>
            {children}
        </TagTypeColourContext.Provider>
    );
};

export const useTagBadgeColours = (tagType?: string | null) => {
    const settings = useContext(TagTypeColourContext);
    const { theme } = useTheme();
    const setting = tagType ? settings[tagType.toLowerCase()] : undefined;
    const configuredBackground = theme === 'dark'
        ? setting?.darkModeColour || DEFAULT_DARK_MODE_COLOUR
        : setting?.lightModeColour || DEFAULT_LIGHT_MODE_COLOUR;
    const fallbackBackground = theme === 'dark' ? DEFAULT_DARK_MODE_COLOUR : DEFAULT_LIGHT_MODE_COLOUR;
    const background = /^#[0-9A-Fa-f]{6}$/.test(configuredBackground)
        ? configuredBackground
        : fallbackBackground;

    return {
        background,
        color: getReadableTextColour(background),
    };
};
